import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { Card } from '@common/ui/Card';
import { Cell } from '@common/ui/Cell';
import { Separator } from '@common/ui/Separator';
import { Toogle } from '@common/ui/Toogle';
import { useEventEmit } from '@common/utils/events';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';

import { usePutNotificationPreferences } from '@services/hooks/UsersService';

const formDataSchema = z.object({
  receive_message_notifications: z.boolean(),
  receive_like_notifications: z.boolean(),
  receive_match_notifications: z.boolean(),
});

type FormData = z.infer<typeof formDataSchema>;

export const NotificationsSettings = () => {
  const emit = useEventEmit();

  const { user } = useAuthStore();
  const { trigger: triggerPutNotificationPreferences } =
    usePutNotificationPreferences();

  const { control } = useForm<FormData>({
    mode: 'onChange',
    resolver: zodResolver(formDataSchema),
    values: user.notification_preferences,
  });

  const formValues = useWatch({ control });

  useEffect(() => {
    try {
      triggerPutNotificationPreferences(formValues);
    } catch {
      emit('error', {
        title: 'Ошибка',
        description: 'Не удалось обновить настройки уведомлений',
      });
    }
  }, [formValues]);

  return (
    <Card testId="notifications-settings" style={{ padding: '0 16px', gap: 2 }}>
      <Controller
        control={control}
        name="receive_message_notifications"
        render={({ field }) => (
          <Cell
            testId="notifications-message"
            title="Сообщения"
            separator={<Separator />}
            suffixIcon={
              <Toogle
                testId="notifications-message-toogle"
                checked={field.value}
                onChange={field.onChange}
              />
            }
          />
        )}
      />
      <Controller
        control={control}
        name="receive_match_notifications"
        render={({ field }) => (
          <Cell
            testId="notifications-match"
            title="Мэтчи"
            separator={<Separator />}
            suffixIcon={
              <Toogle
                testId="notifications-match-toogle"
                checked={field.value}
                onChange={field.onChange}
              />
            }
          />
        )}
      />
      <Controller
        control={control}
        name="receive_like_notifications"
        render={({ field }) => (
          <Cell
            testId="notifications-like"
            title="Вас лайкнули"
            suffixIcon={
              <Toogle
                testId="notifications-like-toogle"
                checked={field.value}
                onChange={field.onChange}
              />
            }
          />
        )}
      />
    </Card>
  );
};
